'use client';

import { Button } from '@formo/ui';
import { CircleAlert, CircleCheck, Info } from 'lucide-react';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastVariant,
  ToastViewport,
  defaultToastVariant,
} from '~/components/ui/toast';
import { useToast } from '~/components/ui/use-toast';

// We use hardcoded colors because the classnames don't work with lucide-react icons
const variantIcons: Record<ToastVariant, React.ReactNode> = {
  success: <CircleCheck size={16} color="#25b24a" strokeWidth={2} />,
  info: <Info size={16} color="#295BFF" strokeWidth={2} />,
  error: <CircleAlert size={16} color="#F2415A" strokeWidth={2} />,
  warning: <CircleAlert size={16} color="#FFBF0F" strokeWidth={2} />,
  noti: <CircleAlert size={16} color="#565960" strokeWidth={2} />,
};

export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(
        ({ id, title, description, action, onAction, variant, ...props }) => (
          <Toast key={id} variant={variant} {...props}>
            <div
              className={`grid ${action ? 'grid-cols-[auto_1fr_auto_auto]' : 'grid-cols-[auto_1fr_auto]'} gap-x-3 gap-y-1 flex-1`}
            >
              {/* Icon - always row 1 column 1 */}
              <div className="row-start-1 col-start-1 self-center">
                {variantIcons[variant ?? defaultToastVariant]}
              </div>

              {/* Title - row 1 column 2 */}
              {title && (
                <div className="row-start-1 col-start-2 self-center">
                  <ToastTitle>{title}</ToastTitle>
                </div>
              )}

              {/* Description - row 2 column 2 (or row 1 if no title) */}
              {description && (
                <div
                  className={`${title ? 'row-start-2' : 'row-start-1'} col-start-2 self-center`}
                >
                  <ToastDescription>{description}</ToastDescription>
                </div>
              )}

              {/* Action - row 1 column 3 (can span to row 2 if description exists) */}
              {action && (
                <div
                  className={`${title && description ? 'row-span-2' : 'row-start-1'} col-start-3 self-center flex items-center`}
                >
                  <Button
                    size={'sm'}
                    variant="text"
                    className="border-content-primary border-1 rounded-full py-2 px-4 mr-3"
                    onClick={onAction}
                  >
                    {action}
                  </Button>
                </div>
              )}

              {/* Close - row 1 column 3 (or column 4 if action exists) */}
              <div
                className={`row-start-1 ${action ? 'col-start-4' : 'col-start-3'} self-center`}
              >
                <ToastClose />
              </div>
            </div>
          </Toast>
        ),
      )}

      <ToastViewport />
    </ToastProvider>
  );
}
